import { useState } from 'react';
import { Alert, Form, Input, Modal, notification } from 'antd';
import CustomButton from 'components/custom-button';
import { refreshPendingCounts } from 'layouts/pending-counts';
import type { StudioCandidate, StudioRequestRow, StudioTeamRow } from '../contract';
import { createStudioRequest, setTeamChannel } from '../api';
import { mapStudioError } from '../studio-error';
import { CHANNEL_NOT_MEMBER_HINT, REASON_MAX, REASON_MIN, REQUEST_SENT_NOTE } from '../labels';
import MemberPicker from './member-picker';

export type TeamChannelTeam = Pick<StudioTeamRow, 'id' | 'name' | 'channelTiktokUid' | 'status'>;

export interface TeamChannelModalProps {
  open: boolean;
  studioId: number;
  team: TeamChannelTeam | null;
  /** admin: Admin đặt ngay (S27). request: Quản lý Studio gửi đề nghị TEAM_CHANNEL (S24). */
  mode: 'admin' | 'request';
  /** set (mặc định): gắn / đổi kênh; clear: bỏ kênh. */
  action?: 'set' | 'clear';
  onClose: () => void;
  onDone?: (result: { team?: StudioTeamRow; request?: StudioRequestRow }) => void;
}

interface FormValues {
  candidate?: StudioCandidate[];
  reason: string;
}

const reasonRules = [
  {
    validator: (_: unknown, value?: string) =>
      (value || '').trim().length >= REASON_MIN
        ? Promise.resolve()
        : Promise.reject(new Error(`Lý do cần ít nhất ${REASON_MIN} ký tự.`)),
  },
];

/**
 * Gắn / đổi / bỏ kênh (UID TikTok) của một Nhóm nhảy (mục 8.7.6). UID kênh CHỈ Admin đặt (R4, S27); Quản lý
 * Studio gửi đề nghị (S24 loại TEAM_CHANNEL) và chỉ chọn được UID đã thuộc Studio (`inThisStudio`, khớp mã
 * STUDIO_CHANNEL_UID_NOT_MEMBER). Form tạo / sửa nhóm không có ô UID kênh.
 */
const TeamChannelModal = ({
  open,
  studioId,
  team,
  mode,
  action = 'set',
  onClose,
  onDone,
}: TeamChannelModalProps) => {
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const clearing = action === 'clear';
  const archivedSet = !clearing && team?.status === 'ARCHIVED';

  const title = (() => {
    if (mode === 'request') return clearing ? 'Đề nghị bỏ kênh' : 'Đề nghị gắn kênh';
    if (clearing) return 'Bỏ kênh';
    return team?.channelTiktokUid ? 'Đổi kênh' : 'Gắn kênh';
  })();

  const disabledReason = (c: StudioCandidate): string | null => {
    if (!c.uid) return 'Người này chưa có UID TikTok.';
    if (team?.channelTiktokUid && c.uid === team.channelTiktokUid) return 'Đang là kênh của nhóm này.';
    if (mode === 'request' && !c.inThisStudio) return CHANNEL_NOT_MEMBER_HINT;
    return null;
  };

  const handleClose = () => {
    if (saving) return;
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: FormValues) => {
    if (!team) return;
    const uid = clearing ? null : values.candidate?.[0]?.uid ?? null;
    if (!clearing && !uid) {
      notification.error({ message: 'Chọn UID TikTok làm kênh.' });
      return;
    }
    const reason = values.reason.trim();
    setSaving(true);
    try {
      if (mode === 'admin') {
        const row = await setTeamChannel(studioId, team.id, { channelTiktokUid: uid, reason });
        notification.success({
          message: clearing ? `Đã bỏ kênh của ${team.name}.` : `Đã gắn kênh cho ${team.name}.`,
        });
        onDone?.({ team: row });
      } else {
        const row = await createStudioRequest(studioId, {
          requestKind: 'TEAM_CHANNEL',
          teamId: team.id,
          uid,
          reason,
        });
        notification.success({ message: 'Đã gửi đề nghị tới Admin.' });
        refreshPendingCounts();
        onDone?.({ request: row });
      }
      form.resetFields();
      onClose();
    } catch (err) {
      notification.error({ message: mapStudioError(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={team ? `${title} · ${team.name}` : title}
      open={open && !!team}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      maskClosable={false}
      width={560}
    >
      {team?.channelTiktokUid && (
        <p style={{ marginTop: 0 }}>
          Kênh hiện tại: <strong>UID {team.channelTiktokUid}</strong>
        </p>
      )}
      {mode === 'request' && (
        <Alert type="info" showIcon message={REQUEST_SENT_NOTE} style={{ marginBottom: 16 }} />
      )}
      {archivedSet && (
        <Alert
          type="warning"
          showIcon
          message="Nhóm nhảy đã lưu trữ chỉ bỏ được kênh."
          style={{ marginBottom: 16 }}
        />
      )}
      <Form form={form} layout="vertical" className="custom-form" onFinish={onFinish}>
        {!clearing && (
          <Form.Item
            label="UID kênh"
            name="candidate"
            extra={
              mode === 'request'
                ? 'Chỉ chọn được Creator đã thuộc Studio.'
                : 'KC của UID này được tính cho Nhóm nhảy thay cho KC từng thành viên.'
            }
            rules={[{ required: true, type: 'array', min: 1, message: 'Chọn UID TikTok làm kênh.' }]}
          >
            <MemberPicker studioId={studioId} disabledReason={disabledReason} disabled={archivedSet} />
          </Form.Item>
        )}
        <Form.Item label="Lý do" name="reason" rules={reasonRules}>
          <Input.TextArea rows={3} maxLength={REASON_MAX} showCount placeholder="Nhập lý do" />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton
            text={mode === 'request' ? 'Gửi đề nghị' : title}
            bgColor={clearing ? 'primary-red' : 'secondary-green'}
            htmlType="submit"
            loading={saving}
            disabled={archivedSet}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default TeamChannelModal;
